import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CompanionPrompt } from '@shared/api/contracts';
import { PERSONALITIES, getSpeciesPersonality } from '@shared/domain/companionMessages';
import { isEgg, getStageName } from '@shared/domain/petHelper';
import { useUserPet } from '../hooks/useUserPet';
import { useWalletConnection } from '../auth/useWalletConnection';
import { getPetStageImageUrlForUI } from '../utils/petHelpers';
import { CompanionMessageModal } from './CompanionMessageModal';
import type { CompanionMessage } from '../hooks/useCompanionMessage';

/**
 * One-time social nudges spoken by the player's own Suikamon: a quick invite to the
 * Telegram community and to follow updates on X. Each is shown exactly once per browser,
 * back to back, using the same pet speech-bubble popup as the companion system so the
 * voice and visuals stay consistent. The external link for each card is resolved by
 * {@link CompanionMessageModal} from its `ctaUrlKey`, so the destination URLs live in one
 * place (no duplicated links here).
 */
const STORAGE_KEY = 'suikamon_social_promo_seen';

/**
 * A single promo card: which external link it points at, its line, and its button label.
 * `eggText` is the pre-hatch variant — an egg can't reference "my clan" or the tricks it
 * hasn't learned yet, so it invites from inside the shell instead.
 */
interface SocialPromo {
  ctaUrlKey: NonNullable<CompanionPrompt['ctaUrlKey']>;
  text: string;
  eggText: string;
  buttonLabel: string;
}

// Telegram first (join the clan), then X (follow what's new). Pet-voiced and short, in
// the warm tone of the companion greetings — the pet is inviting the player, not marketing.
const SOCIAL_PROMOS: SocialPromo[] = [
  {
    ctaUrlKey: 'telegram',
    text: "Psst — my whole clan hangs out on Telegram! Come join us and say hi. It's where I hear all the best gossip. 💛",
    eggText: "*happy wiggle* There's a whole clan waiting to meet me on Telegram — come say hi while I finish hatching! 🥚💛",
    buttonLabel: 'Join Telegram',
  },
  {
    ctaUrlKey: 'x',
    text: "Want to see what we're up to next? Follow us on X for every new trick, treat and surprise — don't miss out! ✨",
    eggText: "I can't wait to show you what I'll become! Follow us on X so you don't miss the moment I hatch. 🥚✨",
    buttonLabel: 'Follow on X',
  },
];

/** True when the one-time social promo hasn't been shown in this browser yet. */
// eslint-disable-next-line react-refresh/only-export-components -- intentional co-located export
export function shouldShowSocialPromo(): boolean {
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage blocked (private mode / quota): don't surface the promo rather than risk
    // re-showing it on every load.
    return false;
  }
}

function markSocialPromoSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Ignore storage failures — worst case the promo reappears on a later visit.
  }
}

interface PromoMessagesProps {
  onClose: () => void;
}

export function PromoMessages({ onClose }: PromoMessagesProps) {
  const { actingAddress } = useWalletConnection();
  const { pet } = useUserPet({ address: actingAddress });
  const [index, setIndex] = useState(0);

  // Mark seen as soon as the sequence actually begins (a pet is loaded to speak the line),
  // so "shown only once" holds even if the player reloads midway through the two cards.
  useEffect(() => {
    if (pet) markSocialPromoSeen();
  }, [pet]);

  const advance = useCallback(() => {
    const next = index + 1;
    if (next >= SOCIAL_PROMOS.length) {
      onClose();
    } else {
      setIndex(next);
    }
  }, [index, onClose]);

  const companion = useMemo<CompanionMessage | null>(() => {
    if (!pet) return null;
    const promo = SOCIAL_PROMOS[index];
    if (!promo) return null;
    // Speak from inside the shell while still an egg: egg-worded line + "Egg" label instead
    // of a personality it hasn't grown into yet.
    const petIsEgg = isEgg(pet.status.stage);
    return {
      petId: pet.pet.id,
      petName: pet.pet.name,
      imageUrl: getPetStageImageUrlForUI(pet.status.species, pet.status.stage),
      personalityLabel: petIsEgg
        ? getStageName(pet.status.stage, pet.status.species)
        : PERSONALITIES[getSpeciesPersonality(pet.status.species)].label,
      message: petIsEgg ? promo.eggText : promo.text,
      kind: 'cta',
      promptInput: { choices: [{ id: 'go', label: promo.buttonLabel }] },
      ctaUrlKey: promo.ctaUrlKey,
      // Following the link and declining both simply advance to the next card / close.
      answer: advance,
      dismiss: advance,
    };
  }, [pet, index, advance]);

  // No pet to speak the line (guest without a free pet, or still loading): show nothing.
  if (!companion) return null;

  // Remount per card so the modal's focus trap and entrance reset for each message.
  return <CompanionMessageModal companion={companion} key={index} />;
}
